import { create } from 'zustand'
import type { UserRole } from '../types/session.types'

/**
 * sessionStore holds the current user's role/permissions in the active war room session.
 * Set when entering a session (from JWT claims or session metadata).
 * Cleared on session exit.
 */
interface SessionContextState {
  sessionId: string | null
  role: UserRole | null
  teamId: string | null
  permissions: string[]

  // Actions
  setSessionContext: (ctx: {
    sessionId: string
    role: UserRole
    teamId?: string
    permissions: string[]
  }) => void
  clearSessionContext: () => void
  hasPermission: (perm: string) => boolean
}

export const useSessionStore = create<SessionContextState>()((set, get) => ({
  sessionId: null,
  role: null,
  teamId: null,
  permissions: [],

  setSessionContext: (ctx) =>
    set({
      sessionId: ctx.sessionId,
      role: ctx.role,
      teamId: ctx.teamId ?? null,
      permissions: ctx.permissions,
    }),

  clearSessionContext: () =>
    set({ sessionId: null, role: null, teamId: null, permissions: [] }),

  hasPermission: (perm) => get().permissions.includes(perm),
}))

// ── Permission helper (mirrors backend RBAC matrix) ─────────────────────────

export const ROLE_PERMISSIONS: Record<string, string[]> = {
  commander: [
    'session:read', 'session:create', 'session:start', 'session:pause',
    'session:complete', 'session:rollback', 'session:export',
    'step:read', 'step:update', 'step:update_any', 'step:approve', 'step:waive',
    'team:join', 'team:manage',
    'preflight:read', 'preflight:check', 'preflight:waive',
    'go_no_go:vote', 'go_no_go:override',
    'report:read', 'audit:read', 'kb:read', 'kb:write',
  ],
  deputy: [
    'session:read', 'session:pause',
    'step:read', 'step:update', 'step:update_any', 'step:approve',
    'team:join', 'team:manage',
    'preflight:read', 'preflight:check',
    'go_no_go:vote',
    'report:read', 'audit:read', 'kb:read',
  ],
  team_lead: [
    'session:read',
    'step:read', 'step:update',
    'team:join',
    'preflight:read', 'preflight:check',
    'go_no_go:vote',
    'report:read', 'kb:read',
  ],
  team_member: [
    'session:read',
    'step:read', 'step:update',
    'team:join',
    'preflight:read', 'preflight:check',
    'report:read', 'kb:read',
  ],
  observer: [
    'session:read', 'step:read', 'team:join',
    'preflight:read', 'report:read', 'audit:read', 'kb:read',
  ],
  auditor: [
    'session:read', 'audit:read', 'audit:export',
    'report:read', 'kb:read',
  ],
}
